import { useMemo } from 'react';
import { useRooms } from '@/shared/hooks/useRooms';
import { useProjectStore } from '@/stores';
import { calculateRoomTotal, calculateGlobalItemsTotals } from '@/entities/estimate';
import { resolveEffectiveRoomGeometry, resolveEffectiveProjectGeometry } from '@/shared/lib/geometry';
import { Room, CalculatedEstimateItem, RoomGeometry, EstimateItem } from '@/shared/model';

export interface CalculatedRoom extends Room {
  displayName: string;
  geometry: RoomGeometry;
  calculations: {
    total: number;
    items: CalculatedEstimateItem[];
  };
}

export interface EstimateResult {
  rooms: CalculatedRoom[];
  activeRoom: CalculatedRoom | null;
  projectItems: CalculatedEstimateItem[];
  projectOverrides: { floorArea?: number; wallArea?: number; perimeter?: number };
  globalTotal: number;
  projectTotal: number;
  totalArea: number;
  safetyMargin: number;
}

/**
 * Хук-селектор для получения рассчитанных данных проекта.
 * Инкапсулирует всю математику сметы.
 */
export function useEstimate(): EstimateResult {
  const estimateData = useRooms();
  const { rooms: rawRooms, activeRoomId, safetyMargin } = estimateData;

  // Project global layer (from store for separation of global logic)
  const projectItems = useProjectStore((s) => s.projectItems);
  const projectOverrides = useProjectStore((s) => s.projectOverrides);

  // Рассчитываем массив комнат. 
  const calculatedRooms = useMemo(() => {
    return rawRooms.map((room: Room) => {
      const geometry = resolveEffectiveRoomGeometry(room);

      const calculations = calculateRoomTotal(room, safetyMargin);

      return {
        ...room,
        displayName: room.displayName || 'Без имени',
        geometry,
        calculations: calculations || { total: 0, items: [] }
      } as CalculatedRoom;
    });
  }, [rawRooms, safetyMargin]);

  // Глобальные + комнаты с поддержкой проектной геометрии (manual overrides на проект).
  // Проектная геометрия резолвится через единый общий резолвер (TRUTH-02) — тот же,
  // что использует экспортный движок (computeTotals.ts) — вместо локальной агрегации.
  const globalCalc = useMemo(() => {
    const resolvedGeometry = resolveEffectiveProjectGeometry(rawRooms, projectOverrides);
    return calculateGlobalItemsTotals(projectItems, safetyMargin, resolvedGeometry);
  }, [projectItems, safetyMargin, projectOverrides, rawRooms]);

  const globalTotal = globalCalc.total;

  // Общие итоги проекта = global + rooms (используем unified где возможно)
  const stats = useMemo(() => {
    const roomsTotal = calculatedRooms.reduce((sum: number, r: CalculatedRoom) => sum + (r.calculations?.total || 0), 0);
    // Для точности можно было бы использовать calculateFullProjectTotal, но rooms уже посчитаны с overrides
    const total = roomsTotal + globalTotal;
    const area = calculatedRooms.reduce((sum: number, r: CalculatedRoom) => sum + (r.geometry?.floorArea || 0), 0);
    
    return { total, area, roomsTotal };
  }, [calculatedRooms, globalTotal]);

  // Активная комната
  // Уважаем явный null (когда пользователь нажал "назад к плитам").
  // Если activeRoomId === null — не авто-выбираем комнату, показываем главную с глобалом.
  const activeRoom = useMemo(() => {
    if (!activeRoomId) return null;
    return calculatedRooms.find((r: CalculatedRoom) => r.id === activeRoomId) || null;
  }, [calculatedRooms, activeRoomId]);

  return {
    rooms: calculatedRooms,
    activeRoom,
    projectItems: globalCalc.items,
    projectOverrides: projectOverrides || {},
    globalTotal: Math.round(globalTotal * 100) / 100,
    projectTotal: Math.round(stats.total * 100) / 100,
    totalArea: stats.area,
    safetyMargin
  };
}
